import AIcon from '@/components/A/AIcon/AIcon'
import cls from 'classnames'
import styles from './ProjectDetailPage.module.less'
import { useEffect, useState } from 'react'
import VideoAnalysis from './components/VideoAnalysis/VideoAnalysis'
import StoryboardAnalysis from './components/StoryboardAnalysis/StoryboardAnalysis'
import HeatMapAnalysis from './components/HeatMapAnalysis/HeatMapAnalysis'
import { useLocation } from 'react-router-dom'
import { PAGE_LINKS } from '@/router/pageConst'
import { useAutoRequest, useRequest } from 'alova/client'
import { api } from '@/api'
import { message } from 'antd'
import { commonUtils } from '@/helpers/commonUtils'
import AButton from '@/components/A/AButton/AButton'
import InfoDrawerBtn from './components/InfoDrawerBtn/InfoDrawerBtn'
import { useTranslation } from 'react-i18next'
import { LanguageEnum } from '@/commonConst'
import AnalyzingView from './components/AnalyzingView/AnalyzingView'
import CategoryTab, { CategoryTabKey } from './components/CategoryTab/CategoryTab'
import { usePollingProgress } from '../ProjectPage/components/ProjectPageContent/ProjectPageContent'
import MiddleText from '@/components/MiddleText/MiddleText'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import { ProjectDetailPageContext } from '@/Contexts/Contexts'
import useNewNavigate from '@/helpers/commonHooks/useNewNavigate'

interface Props {
  onProjectDetailUpdate: (project: ProjectModels.ProjectDetail) => void
}

export function useTranslationChange() {
  const location = useLocation()
  const navigate = useNewNavigate()
  const { i18n, t } = useTranslation()
  const [startPollingTranslateStatus, setStartPollingTranslateStatus] = useState(false)
  const {
    data,
    loading,
    send: request,
  } = useRequest(api.project.projectDetail, {
    immediate: false,
  })
  const sendRequest = async () => {
    return await request(location.state.id, LanguageEnum[i18n.language as keyof typeof LanguageEnum])
  }
  const { data: translateStatusData } = useAutoRequest(
    () =>
      api.project.projectTranslateStatus(
        location.state.id,
        LanguageEnum[i18n.language as keyof typeof LanguageEnum]
      ),
    {
      pollingTime: 1000,
      middleware: (_, next) => {
        if (startPollingTranslateStatus) {
          next()
        }
      },
    }
  )
  useEffect(() => {
    if (location.state && location.state.id) {
      sendRequest()
    } else {
      navigate(PAGE_LINKS.project)
      message.warning(t('dashboard.projectDetailPage.message.projectNotFound'))
    }
  }, [i18n.language])

  useEffect(() => {
    if (data && data.data.progressRatio === 100 && !data.hasTranslated) {
      setStartPollingTranslateStatus(true)
    }
  }, [data])

  useEffect(() => {
    if (translateStatusData && translateStatusData.translateStatus === 'completed') {
      setStartPollingTranslateStatus(false)
      sendRequest()
    }
  }, [translateStatusData])

  return { data, loading, send: sendRequest }
}

export function useAnimationProjects(
  hasTranslated?: boolean,
  project?: ProjectModels.ProjectDetail
) {
  const { i18n } = useTranslation()
  const [animationProjects, setAnimationProjects] = useTypedLocalStorage('animation-projects', {
    defaultValue: [],
  })
  const [canDoAnimation, setCanDoAnimation] = useState<boolean>(false)
  useEffect(() => {
    if (!project) {
      return
    }
    const animations = [...(animationProjects || [])]
    let can = true
    const index = animations.findIndex((p) => p.id === project.id)
    if (animations[index] && animations[index].lng.includes(i18n.language)) {
      can = false
    }
    setCanDoAnimation(can)
    return () => {
      if (hasTranslated !== undefined) {
        if (animations[index]) {
          if (!animations[index].lng.includes(i18n.language)) {
            const lng = [...animations[index].lng, i18n.language]
            animations.splice(index, 1, { ...animations[index], lng })
            setAnimationProjects(animations)
          }
        } else {
          setAnimationProjects([...animations, { id: project.id, lng: [i18n.language] }])
        }
      }
    }
  }, [project, i18n.language])

  useEffect(() => { }, [])
  return canDoAnimation
}


function ProjectDetailPage(props: Props) {
  const { t, i18n } = useTranslation()
  const navigate = useNewNavigate()
  const location = useLocation()
  const { data, send } = useTranslationChange()
  const [project, setProject] = useState<ProjectModels.ProjectDetail>()

  const [tabKey, setTabKey] = useState<CategoryTabKey>('primary')
  const { setStartPollingProgress, progressData } = usePollingProgress(
    project && (project.status === 'analyzing' || project.status === 'creating' || project.status === 'waiting') ? [project.id] : []
  )

  const canDoAnimation = useAnimationProjects(data?.hasTranslated, project)
  const [animationStatus, setAnimationStatus] = useState<{
    videoNeedAnimation: boolean
    storyNeedAnimation: boolean
  }>({
    videoNeedAnimation: false,
    storyNeedAnimation: false,
  })

  const [activeHeatMapIndex, setActiveHeatMapIndex] = useState<number>(0)

  const [initRender, setInitRender] = useState(false)

  const handleRetry = () => {
    api.project
      .projectRetry(project!.id)
      .send()
      .then(() => {
        send()
        setStartPollingProgress(true)
      }).catch((e) => {
        const error = e as ApiError
        if (error.statusCode === 40021) {
          message.error(t('dashboard.projectPage.message.analysisCountNotEnough'))
        }
      })
  }

  const handleClickHeatMap = (index: number) => {
    setTabKey('heatmap')
    setActiveHeatMapIndex(index)
  }

  const handleReturn = () => {
    navigate(location.state?.returnTo || PAGE_LINKS.project)
  }

  const handleProjectDetailUpdate = async () => {
    const { data } = await send()
    props.onProjectDetailUpdate(data)
  }

  useEffect(() => {
    if (data) {
      const nextProject = data.data
      setProject(nextProject)
      if (nextProject.status === 'completed' || nextProject.status === 'failed') {
        props.onProjectDetailUpdate(nextProject)
      }
    }
  }, [data])

  useEffect(() => {
    if (project && (project.status === 'analyzing' || project.status === 'creating' || project.status === 'waiting')) {
      setStartPollingProgress(true)
    }
  }, [project])

  useEffect(() => {
    if (progressData && project && progressData[project.id]) {
      const status =
        progressData[project.id].process === 100 ? progressData[project.id].status : 'analyzing'
      if (status === 'completed' || status === 'failed') {
        send()
      }
    }
  }, [progressData])

  useEffect(() => {
    if (data) {
      const project = data.data
      if (
        progressData[project.id]?.process === undefined ||
        project.status === 'completed' ||
        project.status === 'failed' ||
        (progressData[project.id]?.process === 100 && data.hasTranslated)
      ) {
        setInitRender(true)
      } else {
        setInitRender(false)
      }
    }
  }, [progressData, data])

  useEffect(() => {
    setAnimationStatus({
      storyNeedAnimation: canDoAnimation,
      videoNeedAnimation: canDoAnimation,
    })
  }, [canDoAnimation, i18n.language])

  return (
    <div className={styles.projectDetailPage} id='detail' data-testid='project-detail-page'>
      {project && (
        <>
          <div className={styles.returnBox}>
            <div className={styles.returnBoxInner}>
              <div className={styles.returnBtn} onClick={handleReturn} data-testid='project-detail-return-btn'>
                <span className={styles.iconBox}>
                  <AIcon type='icon-line-global_left' width={14} height={14}></AIcon>
                </span>
                <span className={styles.btnText}>
                  <MiddleText text={t('dashboard.projectDetailPage.testResults')} />
                  {/* {t('dashboard.projectDetailPage.testResults')} */}
                </span>
              </div>
              {(project.status === 'analyzing' || project.status === 'creating') && (
                <div className={cls(styles.badge, styles.ongoing)}>
                  {t('dashboard.projectDetailPage.ongoing')}
                </div>
              )}
              {project.status === 'failed' && (
                <div className={cls(styles.badge, styles.failed)}>
                  {t('dashboard.projectDetailPage.failed')}
                </div>
              )}
              {
                project.status === 'waiting' && (
                  <div className={cls(styles.badge, styles.waiting)}>
                    {t('dashboard.projectDetailPage.waiting')}
                  </div>
                )
              }
            </div>
            <InfoDrawerBtn mode='default' project={project} onProjectUpdate={handleProjectDetailUpdate} />
          </div>
          <div className={styles.detailContentWrapper}>
            <CategoryTab
              onChange={setTabKey}
              activeTab={tabKey}
              hasTranslated={data.hasTranslated}
              project={project}
              onReturn={handleReturn}
              onProjectUpdate={handleProjectDetailUpdate}
            />
            <div className={styles.detailContent}>
              <>
                {initRender && project.status === 'completed' && (
                  <>
                    {tabKey === 'primary' && (
                      <ProjectDetailPageContext.Provider value={{ handleClickHeatMap }}>
                        <VideoAnalysis
                          progressRatio={progressData}
                          project={project}
                          hasTranslated={data.hasTranslated}
                          needDoAnimation={animationStatus.videoNeedAnimation}
                          onAnimationEnd={() => {
                            setAnimationStatus((status) => ({
                              ...status,
                              videoNeedAnimation: false,
                            }))
                          }}
                        />
                      </ProjectDetailPageContext.Provider>
                    )}
                    {tabKey === 'detail' && (
                      <StoryboardAnalysis
                        project={project}
                        hasTranslated={data.hasTranslated}
                        needDoAnimation={animationStatus.storyNeedAnimation}
                        onAnimationEnd={() => {
                          setAnimationStatus((status) => ({
                            ...status,
                            storyNeedAnimation: false,
                          }))
                        }}
                      />
                    )}
                    {tabKey === 'heatmap' && (
                      <HeatMapAnalysis defaultActiveIndex={activeHeatMapIndex} project={project} />
                    )}
                  </>
                )}
                {initRender && project.status === 'failed' && (
                  <div className={styles.failedBox}>
                    <img
                      style={{ width: 140 }}
                      src={commonUtils.getImagePath('projectPage/project-list-empty.png')}
                      alt=''
                    />
                    <p>{t('dashboard.projectDetailPage.videoAnalysisFailed')}</p>
                    <AButton testId='project-detail-retry-btn' type='outlined' shape='round' size='SM' onClick={handleRetry}>
                      {t('dashboard.projectDetailPage.tryAgain')}
                    </AButton>
                  </div>
                )}
                {!initRender && (
                  <AnalyzingView
                    notBegin={project.status === 'waiting'}
                    ratio={
                      progressData[project.id]?.process > 98
                        ? 98
                        : progressData[project.id]?.process
                    }
                  />
                )}
              </>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProjectDetailPage
